/**
 * DatevMailModal — DATEV-Belegtransfer-ZIP per E-Mail an den Steuerberater.
 *
 * Jun-2026 (User-Wunsch): aus `InvoicesPage.jsx` extrahiert und in die
 * Buchhaltung verschoben — DATEV gehört in die Buchhaltung, nicht zu
 * „Rechnungen & Kunden".
 */
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, X, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export default function DatevMailModal({ onClose }) {
    const [toEmail, setToEmail] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [consultantNumber, setConsultantNumber] = useState("");
    const [clientNumber, setClientNumber] = useState("");
    const [saveDefault, setSaveDefault] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.get("/auth/me").then((r) => {
            if (r.data?.tax_advisor_email) setToEmail(r.data.tax_advisor_email);
        }).catch(() => {});
    }, []);

    const send = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const r = await api.post("/invoices/datev-belegtransfer/send-email", {
                to_email: toEmail || undefined,
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
                consultant_number: consultantNumber || undefined,
                client_number: clientNumber || undefined,
                save_default: saveDefault,
            });
            setResult(r.data);
            toast.success(r.data.mocked ? "DATEV-Mail (MOCKED) protokolliert" : "DATEV-Mail versendet");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Versand fehlgeschlagen");
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="datev-mail-modal">
            <div className="bg-white rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
                    <h2 className="font-display font-black text-lg flex items-center gap-2">
                        <Mail className="w-5 h-5" /> DATEV-Belegtransfer an Steuerberater
                    </h2>
                    <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100" data-testid="datev-mail-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {!result ? (
                    <form onSubmit={send} className="p-4 space-y-3">
                        <p className="text-sm text-gray-600">
                            Erstellt das DATEV-Belegtransfer-ZIP (XML-Manifest + PDFs) und versendet es per E-Mail an den Steuerberater.
                        </p>

                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Empfänger-E-Mail</label>
                            <Input
                                type="email"
                                value={toEmail}
                                onChange={(e) => setToEmail(e.target.value)}
                                placeholder="steuerberater@kanzlei.de"
                                className="rounded-sm mt-1"
                                required
                                data-testid="datev-mail-to"
                            />
                            <label className="flex items-center gap-2 mt-1.5 text-[11px] text-gray-600">
                                <input type="checkbox" checked={saveDefault} onChange={(e) => setSaveDefault(e.target.checked)} data-testid="datev-mail-save-default" />
                                Als Standard im Profil speichern (für künftige Versandungen)
                            </label>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Von</label>
                                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm mt-1" data-testid="datev-mail-date-from" />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Bis</label>
                                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm mt-1" data-testid="datev-mail-date-to" />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Beraternr. (DATEV)</label>
                                <Input value={consultantNumber} onChange={(e) => setConsultantNumber(e.target.value)} placeholder="123456" className="rounded-sm mt-1" data-testid="datev-mail-consultant" />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Mandantennr.</label>
                                <Input value={clientNumber} onChange={(e) => setClientNumber(e.target.value)} placeholder="54321" className="rounded-sm mt-1" data-testid="datev-mail-client" />
                            </div>
                        </div>

                        <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-sm text-xs text-amber-900 flex gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0" />
                            <div>Ohne <code>RESEND_API_KEY</code> läuft der Versand im MOCKED-Modus (Inhalt wird im Backend-Log protokolliert).</div>
                        </div>

                        <Button
                            type="submit"
                            disabled={busy}
                            className="w-full bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider"
                            data-testid="datev-mail-submit"
                        >
                            {busy ? "Sende…" : "DATEV-Belegtransfer versenden"}
                        </Button>
                    </form>
                ) : (
                    <div className="p-4 space-y-3" data-testid="datev-mail-result">
                        <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle2 className="w-6 h-6" />
                            <div className="font-display font-black text-lg">
                                {result.mocked ? "Mock-Versand protokolliert" : "Mail versendet"}
                            </div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 text-xs space-y-1">
                            <div className="flex justify-between"><span className="text-gray-500">Empfänger</span><span className="font-bold font-mono-num">{result.to_email}</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">Zeitraum</span><span className="font-bold">{result.period}</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">Rechnungen im ZIP</span><span className="font-bold font-mono-num">{result.invoice_count}</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">ZIP-Größe</span><span className="font-bold font-mono-num">{(result.zip_size_bytes / 1024).toFixed(1)} KB</span></div>
                            {result.saved_as_default && (
                                <div className="pt-2 mt-1 border-t border-gray-200 text-emerald-700 font-bold">✓ Empfänger als Standard im Profil gespeichert</div>
                            )}
                        </div>
                        <Button onClick={onClose} className="w-full bg-[#111827] hover:bg-black text-white rounded-sm uppercase tracking-wider" data-testid="datev-mail-done">
                            Schließen
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
